import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { useSize } from 'ahooks';
import Header from './components/Header';
import { NavType } from './const';
import { Empty } from '@maxtropy/components';
import { cancelFullscreen, goFullscreen, setFitScale } from './utils';
import UnityEnergyCockpitMap from './components/UnityEnergyCockpitMap';
import UnityPowerMap from './components/UnityPowerMap';
import UnityLightMap from './components/UnityLightMap';
import UnityWaterMap from './components/UnityWaterMap';
import UnityEnvironmentMap from './components/UnityEnvironmentMap';
import SharedUnityMapProvider from './components/SharedUnityMapLayer';
import UnityGasMonitorMap from './components/UnityGasMonitorMap';
import { installAssistantButtonHideStyle } from './components/AiAssistant/util';

export type WgqMapMode = 'unityMap' | 'mapView';

export interface ShareDataWrapperProps {
  isFullScreen?: boolean;
  isStandardSize?: boolean;
  loadMap: boolean;
  currentNav: NavType;
  scale?: number;
}

export const ShareDataWrapperContext = createContext<ShareDataWrapperProps>({
  loadMap: false,
  currentNav: NavType.PowerMap,
});

const getDefaultLoadMap = (mapMode: WgqMapMode) => {
  if (mapMode === 'mapView') return true;
  if (typeof window === 'undefined') return false;
  return window.innerWidth > 2560;
};

const getDefaultIsStandardSize = () => {
  if (typeof window === 'undefined') return true;
  return window.innerWidth <= 2560;
};

const WgqAllMaps: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const baseDivRef = useRef<HTMLDivElement>();
  const size = useSize(baseDivRef);
  const resizeRef = useRef<any>();
  const innerDivRef = useRef<HTMLDivElement>();
  const hasInitializedDefaultLoadMapRef = useRef(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false); // 是否全屏
  const [currentNav, setCurrentNav] = useState<NavType>(NavType.EnergyCockpit);
  const [loadMap, setLoadMap] = useState<boolean>(() => getDefaultLoadMap(mapMode));
  const [scale, setScale] = useState<number>(1);
  const shouldUseSharedUnityMap = loadMap && mapMode === 'unityMap';

  useEffect(() => {
    installAssistantButtonHideStyle();
  }, []);

  // 每种大屏的渲染
  const renderContent = useMemo(() => {
    switch (currentNav) {
      case NavType.PowerMap:
        return <UnityPowerMap mapMode={mapMode} />;
      case NavType.EnergyCockpit:
        return <UnityEnergyCockpitMap mapMode={mapMode} />;

      case NavType.LightingMap:
        return <UnityLightMap mapMode={mapMode} />;
      case NavType.WaterMap:
        return <UnityWaterMap mapMode={mapMode} />;
      case NavType.IndustrialGasMap:
        return <UnityGasMonitorMap mapMode={mapMode} />;
      case NavType.EnvironmentalMonitor:
        return <UnityEnvironmentMap mapMode={mapMode} />;
      default:
        return <Empty />;
    }
  }, [currentNav, mapMode]);

  // 判断是否为标准尺寸2560px
  const isStandardSize = useMemo(() => {
    if (!size?.width) return getDefaultIsStandardSize();
    return size.width <= 2560;
  }, [size?.width]);

  const handleResize = () => {
    if (size?.width && size?.width > 1920) {
      const scale = setFitScale(innerDivRef.current, size?.width, size?.height, isFullScreen, isStandardSize);
      setScale(scale ?? 1);
    }
  };

  const handleResizeMemo = useCallback(handleResize, [isFullScreen, isStandardSize, size?.height, size?.width]);

  useEffect(() => {
    if (resizeRef.current) {
      window.removeEventListener('resize', handleResizeMemo);
    }
    resizeRef.current = window.addEventListener('resize', handleResizeMemo);
    handleResizeMemo();
    return () => {
      window.removeEventListener('resize', handleResizeMemo);
    };
  }, [handleResizeMemo]);

  useEffect(() => {
    if (!size?.width || hasInitializedDefaultLoadMapRef.current) return;
    hasInitializedDefaultLoadMapRef.current = true;
    setLoadMap(mapMode === 'mapView' || size.width > 2560);
  }, [mapMode, size?.width]);

  useEffect(() => {
    if (mapMode === 'mapView') {
      setLoadMap(true);
    }
  }, [mapMode]);

  // 全屏
  const handleFullScreen = () => {
    setIsFullScreen(document.fullscreenElement !== null);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreen);
    return () => document.removeEventListener('fullscreenchange', handleFullScreen);
  }, []);

  const fullBtn = () => {
    if (baseDivRef.current) {
      isFullScreen ? cancelFullscreen() : goFullscreen(baseDivRef.current);
    }
  };

  return (
    <div
      className={styles.outer_wrap}
      id="full_screen_box"
      ref={ref => {
        if (ref) {
          baseDivRef.current = ref;
        }
      }}
    >
      <div
        className={styles.inner}
        ref={ref => {
          if (ref) {
            innerDivRef.current = ref;
          }
        }}
      >
        <div className={styles.inner_top}>
          <Header
            fullBtn={fullBtn}
            isFullScreen={isFullScreen}
            isStandardSize={isStandardSize}
            setLoadMap={setLoadMap}
            loadMap={loadMap}
            setCurrentNav={setCurrentNav}
            currentNav={currentNav}
          />
        </div>
        <ShareDataWrapperContext.Provider value={{ isFullScreen, isStandardSize, loadMap, currentNav, scale }}>
          <SharedUnityMapProvider currentNav={currentNav} enabled={shouldUseSharedUnityMap}>
            {renderContent}
          </SharedUnityMapProvider>
        </ShareDataWrapperContext.Provider>
      </div>
    </div>
  );
};

export default WgqAllMaps;
